/**
 * Notification timestamp formatting.
 *
 * Legacy renders `moment.utc(date).local().format('MMMM Do YYYY, h:mm a')`,
 * e.g. "October 8th 2026, 3:42 pm" — the server value is treated as UTC and
 * shown in the viewer's local time.
 *
 * Reproduced with Intl rather than adding `moment` (~70kB) for one call site;
 * see .claude/rules/performance.md on justifying a new runtime dependency. Intl
 * has no ordinal day, so that part is built from Intl.PluralRules, which gets
 * the 1st/2nd/3rd/11th/21st cases right without a lookup table.
 */
const ORDINAL_SUFFIX = { one: 'st', two: 'nd', few: 'rd', other: 'th' };

const ordinalRules = new Intl.PluralRules('en-US', { type: 'ordinal' });

const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long' });
const dayFmt = new Intl.DateTimeFormat('en-US', { day: 'numeric' });
const yearFmt = new Intl.DateTimeFormat('en-US', { year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd". */
export function ordinal(n) {
  return `${n}${ORDINAL_SUFFIX[ordinalRules.select(n)] ?? 'th'}`;
}

/**
 * @param {string} value ISO datetime from the API (UTC)
 * @returns {string} e.g. "October 8th 2026, 3:42 pm" — empty string if unusable,
 *   matching legacy's `if (!date) return ''` rather than rendering "Invalid Date".
 */
export function formatNotificationDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = ordinal(Number(dayFmt.format(date)));
  // Intl gives "3:42 PM"; legacy's `a` token is lowercase.
  const time = timeFmt.format(date).toLowerCase();

  return `${monthFmt.format(date)} ${day} ${yearFmt.format(date)}, ${time}`;
}
